import type { SupabaseClient } from '@supabase/supabase-js'

export type BookingStatus = 'Ausstehend' | 'Bestätigt' | 'Storniert' | 'Walk-in' | 'Anwesend' | 'Abgereist'

export interface Booking {
  id: string
  restaurant_id: string
  guest_first_name: string
  guest_last_name?: string | null
  guest_email?: string | null
  guest_phone?: string | null
  date: string // YYYY-MM-DD
  time: string // HH:MM:SS or HH:MM
  guests: number
  notes?: string | null
  status: BookingStatus
  table_name?: string | null
  created_at?: string
  updated_at?: string
}

export interface NewBooking {
  restaurant_id: string
  guest_first_name: string
  guest_last_name?: string
  guest_email?: string
  guest_phone?: string
  date: string
  time: string
  guests: number
  notes?: string
  status?: BookingStatus
  table_name?: string
}

export async function listBookingsByDate(
  supabase: SupabaseClient,
  restaurantId: string,
  dateISO: string,
): Promise<Booking[]> {
  const { data, error } = await supabase
    .from('bookings')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .eq('date', dateISO)
    .order('time', { ascending: true })
  if (error) {
    console.error('listBookingsByDate', error)
    return []
  }
  return (data ?? []) as Booking[]
}

export async function createBooking(
  supabase: SupabaseClient,
  input: NewBooking,
  opts?: { notifySlug?: string },
): Promise<{ data: Booking | null; error: string | null }> {
  // No .select() / .single() here on purpose: this is called from the public
  // widget with the anonymous Supabase client, which has INSERT permission
  // via the "public insert bookings" policy but no SELECT permission on
  // bookings — adding RETURNING would fail RLS even though the row inserted.
  const { error } = await supabase
    .from('bookings')
    .insert({ ...input, status: input.status ?? 'Ausstehend' })
  if (error) return { data: null, error: error.message }
  // Fire-and-forget push notification to all owner devices. We don't
  // await — the booking is already saved, push failures shouldn't
  // block the guest's confirmation screen.
  if (opts?.notifySlug && typeof fetch !== 'undefined') {
    fetch('/api/push/notify-booking', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ restaurant_slug: opts.notifySlug }),
      keepalive: true,
    }).catch(() => { /* best effort */ })
  }
  return { data: null, error: null }
}

export async function updateBooking(
  supabase: SupabaseClient,
  id: string,
  patch: Partial<Omit<Booking, 'id' | 'restaurant_id' | 'created_at' | 'updated_at'>>,
): Promise<{ error: string | null }> {
  const { error } = await supabase.from('bookings').update(patch).eq('id', id)
  return { error: error?.message ?? null }
}

export async function deleteBooking(supabase: SupabaseClient, id: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from('bookings').delete().eq('id', id)
  return { error: error?.message ?? null }
}

// Generate time slots between open and close (30-minute intervals)
export function generateTimeSlots(open: string, close: string, intervalMin = 30): string[] {
  if (!open || !close) return []
  const [openH, openM] = open.split(':').map(Number)
  const [closeH, closeM] = close.split(':').map(Number)
  const start = openH * 60 + openM
  let end = closeH * 60 + closeM
  // Past-midnight closing (e.g. 09:00–01:00, common for bars/late kitchens):
  // end < start made the loop run zero times, so the widget showed NO times
  // at all. Clamp to midnight — offering same-day slots up to 23:30 is what
  // the restaurant means; after-midnight slots would carry the wrong date.
  if (end <= start) end = 24 * 60
  const slots: string[] = []
  for (let m = start; m <= end - intervalMin; m += intervalMin) {
    const h = Math.floor(m / 60)
    const min = m % 60
    slots.push(`${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`)
  }
  return slots
}

// JS Day (0=Sun, 1=Mon..) → key in OpeningHours JSONB (German names matching onboarding)
const DAY_NAMES = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']

export interface DayHours { open: string; close: string; active: boolean }

export function dayHoursForDate(openingHours: Record<string, DayHours> | null | undefined, date: Date): DayHours | null {
  if (!openingHours) return null
  const dayName = DAY_NAMES[date.getDay()]
  const dh = openingHours[dayName]
  if (!dh || !dh.active) return null
  return dh
}
